import { useState, useEffect, useCallback } from "react"
import MuiSlider from "@mui/material/Slider"
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded"
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded"
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew"
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos"
import "./slider.css"
import "leaflet/dist/leaflet.css"
import Badge from '@mui/material/Badge';
/**
 * Formats a date for GeoServer API consumption
 * @param {Date} date - The date to format
 * @returns {string} - ISO string formatted for GeoServer
 */
function formatDateForGeoServer(date) {
  date.setMinutes(0, 0, 0, 0)
  return date.toISOString().replace(/:\d{2}\.\d{3}Z$/, ":00Z")
}

/**
 * Formats a date for display to the user
 * @param {Date} date - The date to format
 * @returns {string} - Human-readable date string
 */
function formatDateForDisplay(date) {
  const year = date.getUTCFullYear()
  const month = (date.getUTCMonth() + 1).toString().padStart(2, "0")
  const day = date.getUTCDate().toString().padStart(2, "0")
  const hours = date.getUTCHours().toString().padStart(2, "0")
  const minutes = date.getUTCMinutes().toString().padStart(2, "0")
  const seconds = date.getUTCSeconds().toString().padStart(2, "0")
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} UTC`
}

/**
 * Debounce utility function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
function debounce(func, wait) {
  let timeout
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(this, args), wait)
  }
}

/**
 * TimeSlider Component
 * A responsive time slider for forecast data visualization
 */
function TimeSlider({ onTimeChange, fetchGeoJSON, selectedCategory, forecastValue }) {
  // State management
  const [sliderValue, setSliderValue] = useState(0)
  const [isAutoPlay, setIsAutoPlay] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [adjustedDateTime, setAdjustedDateTime] = useState("")
  const [isAlertVisible, setIsAlertVisible] = useState(true)
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  )

  // Debounced fetch function
  const debouncedFetchGeoJSON = useCallback(
    debounce((geoServerDateTime, category) => {
      fetchGeoJSON(geoServerDateTime, category)
    }, 500),
    []
  )

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Update slider when forecastValue changes
  useEffect(() => {
    setSliderValue(0)
  }, [forecastValue])

  // Handle play/stop button click
  const handlePlayButtonClick = () => {
    setIsAutoPlay(!isAutoPlay)
    setIsPlaying(!isPlaying)
    setIsAlertVisible(true)
  }

  // Handle stepping back and forward
  const handleStepBack = () => {
    setSliderValue((prev) => Math.max(0, prev - 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
      setIsPlaying(false)
    }
  }

  const handleStepForward = () => {
    setSliderValue((prev) => Math.min(48, prev + 1))
    if (isAutoPlay) {
      setIsAutoPlay(false)
      setIsPlaying(false)
    }
  }

  // Handle alert close
  const handleCloseAlert = () => {
    setIsAlertVisible(false)
  }

  // Stop autoplay when slider is moved manually
  const handleChangeCommitted = () => {
    if (isAutoPlay) {
      setIsAutoPlay(false)
      setIsPlaying(false)
    }
  }

  // Handle slider value change
  const handleSliderChange = (event, value) => {
    setSliderValue(value)
    const forecastStartDate = new Date(forecastValue)
    forecastStartDate.setUTCHours(forecastStartDate.getUTCHours() + value)

    const geoServerDateTime = formatDateForGeoServer(forecastStartDate)
    setAdjustedDateTime(formatDateForDisplay(forecastStartDate))
    setIsAlertVisible(true)
    onTimeChange(geoServerDateTime)
  }

  // Update time when slider value or forecast value changes
  useEffect(() => {
    const forecastStartDate = new Date(forecastValue)
    forecastStartDate.setUTCHours(forecastStartDate.getUTCHours() + sliderValue)
    const geoServerDateTime = formatDateForGeoServer(forecastStartDate)
    onTimeChange(geoServerDateTime)
    setAdjustedDateTime(formatDateForDisplay(forecastStartDate))

    const timer = setTimeout(() => setIsAlertVisible(false), 5000)
    return () => clearTimeout(timer)
  }, [sliderValue, forecastValue, onTimeChange])

  // Autoplay functionality
  useEffect(() => {
    if (isAutoPlay) {
      const interval = setInterval(() => {
        setSliderValue((prevValue) => {
          const nextValue = prevValue < 48 ? prevValue + 1 : 0
          const newDateTime = new Date(forecastValue)
          newDateTime.setUTCHours(newDateTime.getUTCHours() + nextValue)

          const newGeoServerDateTime = formatDateForGeoServer(newDateTime)
          onTimeChange(newGeoServerDateTime)
          setAdjustedDateTime(formatDateForDisplay(newDateTime))

          return nextValue
        })
      }, 1300)

      return () => clearInterval(interval)
    }
  }, [isAutoPlay, forecastValue, onTimeChange])

  // Generate slider marks
  const generateMarks = () => {
    const marks = []
    for (let i = 0; i <= 48; i += 6) {
      marks.push({
        value: i,
        label: windowWidth <= 768 && i % 12 !== 0 ? "" : `+${i}h`
      })
    }
    return marks
  }

  // Value label formatter
  const formatValueLabel = (value) => {
    const forecastStartDate = new Date(forecastValue)
    forecastStartDate.setHours(forecastStartDate.getHours() + value)
    return formatDateForDisplay(forecastStartDate)
  }

  return (
    <div className="time-slider-container">
      {/* Play/Stop Button */}
      <button
        className={`ts-custom-btn ${isAutoPlay ? 'ts-custom-btn--stop' : 'ts-custom-btn--play'}`}
        onClick={handlePlayButtonClick}
        aria-label={isAutoPlay ? "Stop autoplay" : "Start autoplay"}
      >
        {isAutoPlay ? (
          <StopCircleRoundedIcon />
        ) : (
          <PlayCircleOutlineRoundedIcon />
        )}
      </button>

      {/* Step Back Arrow (Mobile only) */}
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepBack}
          aria-label="Step back 1 hour"
        >
          <ArrowBackIosNewIcon />
        </button>
      )}

      {/* Time Slider */}
      <MuiSlider
        className="time-slider-horizontal"
        min={0}
        max={48}
        value={sliderValue}
        step={1}
        disabled={false}
        onChange={handleSliderChange}
        onChangeCommitted={handleChangeCommitted}
        marks={generateMarks()}
        valueLabelDisplay="on"

        // 📍 INYECCIÓN DEL BADGE CON TU FUNCIÓN DE FORMATO ORIGINAL
        valueLabelFormat={(value) => {
          const fechaFormateada = formatValueLabel(value); // O tu cálculo manual según el slider

          // CONDICIÓN: Solo mostramos el contenido del Badge si el slider está en la posición 0
          const mostrarBadge = sliderValue === 0;

          return (
            <Badge
              // Si mostrarBadge es true, pinta "ISSUE TIME". Si es false, lo dejamos vacío u oculto
              badgeContent={mostrarBadge ? "ISSUE TIME" : null}
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  right: -10,
                  top: -12,
                  fontSize: '0.55rem',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  padding: '0 4px',
                  zIndex: 16000,

                  // Combinamos ambas condiciones en el CSS:
                  // Si mostrarBadge es false, metemos 'none' en todos lados para asegurar que desaparezca
                  display: mostrarBadge
                    ? { xs: 'flex', sm: 'none' }
                    : 'none'
                }
              }}
            >
              <span>{fechaFormateada}</span>
            </Badge>
          );
        }}

        aria-label="Time slider"
        sx={{
          // Only essential overrides that can't be handled by CSS
          '& .MuiSlider-valueLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
            // Permite que el Badge flote por fuera sin cortarse
            overflow: 'visible !important',
          },
          '& .MuiSlider-markLabel': {
            fontSize: windowWidth <= 350 ? '0.6rem' :
              windowWidth <= 500 ? '0.65rem' :
                windowWidth <= 768 ? '0.7rem' : '0.75rem',
          }
        }}
      />

      {/* Step Forward Arrow (Mobile only) */}
      {windowWidth <= 768 && (
        <button
          className="ts-nav-btn"
          onClick={handleStepForward}
          aria-label="Step forward 1 hour"
        >
          <ArrowForwardIosIcon />
        </button>
      )}
    </div>
  )
}

export default TimeSlider